import { useEffect, useState } from "react";
import api, { fmtRM, fmtDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, Plane, Hotel, Globe, Armchair } from "lucide-react";

const CATS = { umrah: "Umrah", hajj: "Hajj", muslim_tour: "Muslim Tour", domestic: "Domestic" };
const CAT_STYLE = {
  umrah: "bg-emerald-50 text-emerald-700 border-emerald-200",
  hajj: "bg-gold-100 text-gold-700 border-gold-500/30",
  muslim_tour: "bg-sky-50 text-sky-700 border-sky-200",
};

export default function Storefront() {
  const { lang, toggle } = useLang();
  const [store, setStore] = useState(null);
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ departure_id: "", name: "", phone: "", email: "", pax: 1, room_type: "Quad" });

  useEffect(() => {
    api.get("/public/store").then((r) => setStore(r.data)).catch(() => toast.error("Store unavailable"));
  }, []);

  const openBooking = (pkg) => {
    setSelected(pkg);
    setDone(null);
    setForm({ departure_id: "", name: "", phone: "", email: "", pax: 1, room_type: "Quad" });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post("/public/bookings", {
        package_id: selected.package_id, departure_id: form.departure_id,
        name: form.name, phone: form.phone, email: form.email || null,
        pax: Number(form.pax), room_type: form.room_type,
      });
      setDone(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!store) return <div className="min-h-screen bg-sand flex items-center justify-center" data-testid="store-loading"><div className="w-10 h-10 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-sand text-slate-900" data-testid="store-page">
      <header className="bg-white/85 backdrop-blur-md border-b border-sand-border sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/logo.jpeg" alt="Travel Hub Desk" className="w-9 h-9 rounded-lg object-cover" />
            <div className="leading-none">
              <span className="font-serif text-lg tracking-tight block">Travel Hub Desk</span>
              <span className="text-[9px] font-mono uppercase tracking-[0.15em] text-slate-400">{store.agency}</span>
            </div>
          </div>
          <button data-testid="store-language-toggle" onClick={toggle}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-slate-500 hover:text-emerald-800 px-3 py-1.5 rounded-full border border-sand-border bg-white">
            <Globe className="w-3.5 h-3.5" />{lang === "en" ? "BM" : "EN"}
          </button>
        </div>
      </header>

      <section className="bg-brand-950 text-sand py-14">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gold-500 mb-3">{lang === "bm" ? "TEMPAHAN ONLINE" : "ONLINE BOOKING"}</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            {lang === "bm" ? "Tempah Pakej Umrah, Haji & Pelancongan Muslim" : "Book Umrah, Hajj & Muslim Tour Packages"}
          </h1>
          <p className="text-sand/60 mt-4 max-w-2xl text-sm sm:text-base">
            {lang === "bm"
              ? "Pilih pakej, semak kekosongan seat secara langsung, dan tempah dalam masa 2 minit. Pasukan kami akan hubungi anda untuk bayaran."
              : "Choose a package, check live seat availability, and book in 2 minutes. Our team will contact you for payment."}
          </p>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {store.packages.map((p) => (
            <div key={p.package_id} data-testid={`store-package-${p.package_code}`}
              className="pro-card overflow-hidden flex flex-col">
              <div className="bg-brand-950 p-5 relative">
                <Badge className={`border ${CAT_STYLE[p.category] || CAT_STYLE.umrah} absolute top-4 right-4`}>{CATS[p.category] || p.category}</Badge>
                <div className="text-[10px] font-mono text-gold-500 uppercase tracking-[0.2em]">{p.package_code}</div>
                <h3 className="font-serif text-xl text-sand mt-1.5 pr-16 leading-snug">{p.name}</h3>
                <div className="text-sand/50 text-xs mt-1">{p.destination} • {p.duration}</div>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <div className="space-y-2 text-xs text-muted-foreground flex-1">
                  <div className="flex items-center gap-1.5"><Plane className="w-3.5 h-3.5 text-brand-700" />{p.airline || "—"}</div>
                  <div className="flex items-center gap-1.5 truncate"><Hotel className="w-3.5 h-3.5 text-gold-600" />{p.hotel_makkah || "—"}</div>
                  <div className="flex items-center gap-1.5 truncate"><Hotel className="w-3.5 h-3.5 text-brand-500" />{p.hotel_madinah || "—"}</div>
                </div>
                <div className="mt-4 pt-4 border-t border-sand-border">
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">{lang === "bm" ? "bermula" : "from"}</div>
                      <div className="font-sans text-2xl font-bold text-slate-900 tabular-nums">{fmtRM(p.price)}</div>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Armchair className="w-3.5 h-3.5" />
                      {p.departures.reduce((s, d) => s + d.available, 0)} seats
                    </div>
                  </div>
                  <Button data-testid={`store-book-${p.package_code}`} onClick={() => openBooking(p)}
                    disabled={p.departures.length === 0}
                    className="w-full bg-emerald-800 hover:bg-emerald-700 text-white rounded-full font-semibold">
                    {p.departures.length === 0 ? (lang === "bm" ? "Tiada Pelepasan" : "No Departures") : (lang === "bm" ? "Tempah Sekarang" : "Book Now")}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>

      <footer className="border-t border-sand-border py-8 text-center text-xs text-slate-400">
        © 2026 Travel Hub Desk • {store.agency}
      </footer>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent data-testid="store-booking-dialog" className="sm:max-w-md">
          {done ? (
            <div className="text-center py-4" data-testid="store-booking-success">
              <div className="w-16 h-16 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="font-serif text-2xl text-slate-900 mt-4">{lang === "bm" ? "Tempahan Diterima!" : "Booking Received!"}</h2>
              <div className="bg-sand rounded-xl p-4 mt-5 text-sm space-y-2 text-left">
                <div className="flex justify-between"><span className="text-muted-foreground">Booking No</span><span className="font-mono font-semibold" data-testid="store-booking-no">{done.booking_no}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Package</span><span className="text-right">{done.package_name}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Departure</span><span>{fmtDate(done.departure_date)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-mono font-semibold text-emerald-700">{fmtRM(done.total)}</span></div>
              </div>
              <p className="text-xs text-muted-foreground mt-4">
                {lang === "bm" ? "Pasukan kami akan menghubungi anda untuk pengesahan & bayaran deposit." : "Our team will contact you for confirmation & deposit payment."}
              </p>
              <Button data-testid="store-done-button" onClick={() => setSelected(null)} className="mt-5 bg-emerald-800 hover:bg-emerald-700 text-white rounded-full px-8">Done</Button>
            </div>
          ) : (
            <>
              <DialogHeader><DialogTitle className="font-sans font-semibold text-lg">{lang === "bm" ? "Tempah" : "Book"}: {selected?.name}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>{lang === "bm" ? "Tarikh Pelepasan" : "Departure Date"}</Label>
                  <Select value={form.departure_id} onValueChange={(v) => setForm({ ...form, departure_id: v })} required>
                    <SelectTrigger data-testid="store-departure-select"><SelectValue placeholder={lang === "bm" ? "Pilih tarikh" : "Select date"} /></SelectTrigger>
                    <SelectContent>
                      {(selected?.departures || []).map((d) => (
                        <SelectItem key={d.departure_id} value={d.departure_id} disabled={d.available <= 0}>
                          {fmtDate(d.date)} — {d.available > 0 ? `${d.available} seats left` : "SOLD OUT"} — {fmtRM(d.price || selected.price)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>{lang === "bm" ? "Nama Penuh" : "Full Name"}</Label>
                    <Input data-testid="store-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                  <div><Label>{lang === "bm" ? "Telefon" : "Phone"}</Label>
                    <Input data-testid="store-phone-input" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+6012…" /></div>
                  <div><Label>Email</Label>
                    <Input data-testid="store-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  <div><Label>Pax</Label>
                    <Input data-testid="store-pax-input" type="number" min="1" max="10" value={form.pax} onChange={(e) => setForm({ ...form, pax: e.target.value })} /></div>
                  <div><Label>{lang === "bm" ? "Jenis Bilik" : "Room Type"}</Label>
                    <Select value={form.room_type} onValueChange={(v) => setForm({ ...form, room_type: v })}>
                      <SelectTrigger data-testid="store-room-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{["Quad", "Triple", "Double", "Single"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select></div>
                </div>
                {form.departure_id && (
                  <div className="bg-sand rounded-xl p-3 text-sm flex justify-between" data-testid="store-total-preview">
                    <span className="text-muted-foreground">{lang === "bm" ? "Anggaran jumlah" : "Estimated total"}</span>
                    <span className="font-mono font-semibold">{fmtRM((selected?.price || 0) * (Number(form.pax) || 1))}</span>
                  </div>
                )}
                <Button data-testid="store-submit-button" type="submit" disabled={submitting || !form.departure_id}
                  className="w-full bg-emerald-800 hover:bg-emerald-700 text-white rounded-full font-semibold">
                  {submitting ? "…" : (lang === "bm" ? "Hantar Tempahan" : "Submit Booking")}
                </Button>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
